import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parse } from '../util/urlHelpers';

/**
 * The location suggestions should be ranked around (RAS-2536).
 *
 * Mirrors the precedence the search page itself uses when it builds `aroundLatLng`
 * (AlgoliaSearchPage.duck.js): the `origin` query param the customer explicitly chose wins,
 * and their detected location is the fallback. Suggestions ranked around a different point
 * than the results would be actively confusing.
 *
 * Read here rather than threaded through props because the search input sits three components
 * below wherever the location is known, on every surface that renders it.
 *
 * @returns {string|null} "lat,lng", or null when no location is known — in which case the
 *   lookup skips its nearby pass and searches everywhere.
 */
export const useSuggestionOrigin = () => {
  const { search } = useLocation();
  const userOrigin = useSelector(state => state.user?.currentUserLocationInfo?.origin);

  return useMemo(() => {
    const { origin } = parse(search, { latlng: ['origin'] });

    if (Number.isFinite(origin?.lat) && Number.isFinite(origin?.lng)) {
      return `${origin.lat},${origin.lng}`;
    }
    if (Number.isFinite(userOrigin?.lat) && Number.isFinite(userOrigin?.lng)) {
      return `${userOrigin.lat},${userOrigin.lng}`;
    }
    return null;
  }, [search, userOrigin]);
};
